<template>
  <AppModal
    v-if="courseSet"
    :use-dimness="true"
    :no-body-overflow="false"
    @close="$emit('close', false)"
  >
    <template #modal-header>
      <h3
        id="modal-headline"
        class="text-lg leading-6 font-medium text-gray-900"
      >
        {{ courseSet.name || `Untitled ${capitalize($localize('course set'))}` }}
      </h3>
    </template>
    <template #modal-body>
      <div
        v-if="courseList && courseList.length"
        class="w-full"
      >
        <h4
          class="pt-0"
          style="margin-top: 0 !important;"
        >Courses ({{courseList.length }})</h4>
        <LogicTree
          v-if="courseSet.type === 'static' && hasChildren"
          class="mt-2"
          :value="nestedStructure"
        >
          <template #default="{ value: rule }">
            <nuxt-link
              v-if="rule.value"
              :to="`/courses/${encodeURIComponent(coursesObject[rule.value].courseGroupId)}`"
            >
              {{ generateCourseLabel(coursesObject[rule.value]) }}
            </nuxt-link>
            <span
              v-else
              class="opacity-75"
            >Not set</span>
          </template>
        </LogicTree>
        <BaseTable
          v-else
          class="pt-4"
          :rows="courseListSorted"
          resource-name="Courses"
          @select="goToCourse"
        >
          <template #header>
            <th class="th w-48">Course Code</th>
            <th class="th w-96">Course Name</th>
          </template>
          <template #default="{ row: course }">
            <td class="align-middle w-48">{{ course.code || course.subjectCode + course.courseNumber }}</td>
            <td class="align-middle w-96">{{ courseNameWithFallbacks(course) }}</td>
          </template>
        </BaseTable>
      </div>
      <div v-else>
        <p>It looks like we weren't able to find any courses that fit the criteria you selected.</p>
      </div>
    </template>
    <template #modal-footer>
      <span class="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:w-auto">
        <button
          type="button"
          aria-label="Close modal"
          class="inline-flex justify-center w-full rounded-md border border-gray-300 px-4 py-2 bg-white text-base leading-6 font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue transition ease-in-out duration-150 sm:text-sm sm:leading-5"
          @click="$emit('close', false)"
        >
          Close
        </button>
      </span>
    </template>
  </AppModal>
</template>

<script>
import _sortBy from 'lodash/sortBy'
import AppModal from '@/components/AppModal'
import LogicTree from '@/components/LogicTree'
import useCourses from '@/composables/useCourses'
import { generateCourseLabel } from '@/utils/dataLabels'
import { courseNameWithFallbacks } from '@/utils/entityNames'
import { capitalize } from '@/utils/generalUtils'

export default {
  components: { AppModal, LogicTree },
  model: { prop: 'courseSet', event: 'close' },
  props: {
    courseSet: {
      type: [Object, Boolean],
      required: true,
    },
    courseList: {
      type: Array,
      default: () => [],
    },
  },
  setup(props, { root }) {
    const { goToCourse } = useCourses(root)
    return { goToCourse, generateCourseLabel, courseNameWithFallbacks }
  },
  methods: {
    capitalize
  },
  computed: {
    hasChildren() {
      if (!this.nestedStructure) return false
      return (this.nestedStructure.rules || []).some(
        (rule) => rule.rules && rule.rules.length
      )
    },
    courseListSorted() {
      return _sortBy(this.courseList, ['code', 'name', 'longName'])
    },
    nestedStructure() {
      return this.courseSet.structure
    },
    coursesObject() {
      return this.courseListSorted.reduce((acc, course) => {
        acc[course.courseGroupId] = course
        return acc
      }, {})
    },
  },
}
</script>
